import { Injectable } from '@angular/core';
import { type AbstractControl, type ValidationErrors, type ValidatorFn } from '@angular/forms';
import { type FormControl, I18nService, TimeService, UNPLACEHOLDER } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { CALENDAR_LAST_DAY } from 'app/common/constants';
import { isUndefined } from 'lodash';

import { FrequenciesE, HyperCdpConfigPolicySHourValue, HyperCdpConfigPolicySNumberRange, MAX_HYPER_CDP_CONFIG_POLICY_SMOUTHLY_DAY } from './config-policy.const';

/**
 * 最小保护周期单位枚举
 */
export enum protectPeriodUnitE {
    day = '46',
    month = '47',
    year = '48'
}

/**
 * 最小保护周期范围相关常量
 */
export enum protectPeriodRangeE {
    min = '1',
    maxYear = '20',
    maxMonth = '240',
    maxDay = '7300',
    defaultMonth = '12',
    defaultYear = '20',
    defaultDay = '1'
}

/**
 * 定时HyperCDP计划相关常量
 */
export enum ScheduleNumE {
    /**
     * 未配置时的值未0
     */
    DefaultNum = 0,
    /**
    定时计划最大保留数
     */
    MaxNum = 10000
}

@Injectable({ providedIn: 'any' })
export class ConfigPolicyStrService {
    table = {
        createBtn: this.get('common.create_button'),
        previousBtn: this.get('common.previous_button'),
        nextBtn: this.get('common.next_button'),
        okBtn: this.get('common.confirm_button'),
        cancelBtn: this.get('common.cancel_button'),
    };
    title = {
        date: this.get('common.lower_data_label'),
        time: this.get('common.time_label'),
        lastday: this.get('protection.HyperCDPSchedule_task_create_step2_monthlyExecute_lastDay_lab'),
        modify: this.get('protection.HyperCDPSchedule_task_modify_title'),
        basicInfo: this.get('common.basicInfo_title'),
        schedule: this.get('common.plan_title'),
        name: this.get('common.name_column'),
        description: this.get('common.description_column'),
        specificTime: this.get('protection.HyperCDPSchedule_task_create_step2_sepcifyTime_lab'),
        fixedPeriod: this.get('protection.HyperCDPSchedule_task_create_step2_cycle_lab'),
        every: this.get('common.everyPrep_label'),
        hour: this.get('common.timeAcount_hours_label'),
        hourLabel: this.get('common.time_hour_label'),
        minute: this.get('common.timeAcount_minutes_label'),
        second: this.get('common.time_seconds_label'),
        minuteLabel: this.get('common.time_minute_label'),
        day: this.get('common.day_singular_unit'),
        days: this.get('common.lower_days_label'),
        week: this.get('common.week_label'),
        retention: this.get('protection.HyperCDPSchedule_task_create_step2_backupPolicy_retention_lab'),
        rangeResult: (value1: number, value2: number): string => this.get('common.valueRange_label', [value1, value2]),
        everyByTime: this.get('protection.HyperCDPSchedule_task_create_step2_backupPolicy_retentionPeriod_lab'),
        everyByCount: this.get('protection.HyperCDPSchedule_task_create_step2_backupPolicy_retentionNum_lab'),
        retainedNumber: this.get('protection.HyperCDPSchedule_task_create_step2_numofsavingPerMember_lab'),
        daily: this.get('protection.HyperCDPSchedule_task_create_step2_dailyExecute_checkbox'),
        weekly: this.get('protection.HyperCDPSchedule_task_create_step2_weeklyExecute_checkbox'),
        monthly: this.get('protection.HyperCDPSchedule_task_create_step2_monthlyExecute_lab'),
        sun: this.get('common.seven_checkbox'),
        mon: this.get('common.one_checkbox'),
        tues: this.get('common.two_checkbox'),
        wed: this.get('common.three_checkbox'),
        thrs: this.get('common.four_checkbox'),
        fri: this.get('common.five_checkbox'),
        sat: this.get('common.six_checkbox'),
        eachLab: this.get('settings.Antivirus_task_oneDemandScanPolicy_timePolicy_every_lab'),
        mouthlyLab: this.get('common.month_singular_unit'),
        weeklyLab: this.get('protection.HyperCDPSchedule_task_create_week_value_simple'),
        retainLab: this.get('protection.HyperCDPSchedule_task_create_reservedSnap_info1_simple'),
        copiesNumberLab: this.get('protection.HyperCDPSchedule_task_create_reservedSnap_info2_simple'),
        placeHolderContext: this.get('common.select_lab'),
        timePolicyLab: this.get('settings.Antivirus_task_oneDemandScanPolicy_timePolicy_PeriodPolicy_lab'),
        addBtn: this.get('common.add_button'),
        // 保留
        retain: this.get('protection.HyperCDPSchedule_task_create_step3_executePolicy_retained_lab'),
        // "{0}个（{1}天{2}小时{3}分{4}秒）",
        etentionPeriod: this.get('protection.HyperCDPSchedule_task_create_step3_executePolicy_etentionPeriod_lab'),
        // "{0}个（{1}天{2}小时）",
        etentionPeriodTime: this.get('protection.HyperCDPSchedule_task_create_step3_executePolicy_etentionPeriod_hour_lab'),
        // : "每{0} {1}执行一次"
        executePolicyTime: this.get('protection.HyperCDPSchedule_task_create_step3_executePolicy_time_lab'),
        // '每周{0} {1}执行一次',
        executePolicyWeek: this.get('protection.HyperCDPSchedule_task_create_step3_executePolicy_week_lab'),
        // '每{0}小时执行一次'
        executePolicyHour: this.get('protection.HyperCDPSchedule_task_create_step3_executePolicy_hour_lab'),
        // '每{0}分钟执行一次'
        executePolicyMin: this.get('protection.HyperCDPSchedule_task_create_step3_executePolicy_min_lab'),
        // '每{0}秒执行一次'
        executePolicySec: this.get('protection.HyperCDPSchedule_task_create_step3_executePolicy_s_lab'),
        // '每天 {0}执行一次'
        executePolicyDay: this.get('protection.HyperCDPSchedule_task_create_step3_executePolicy_day_lab'),
        // '每月{0}{1}执行一次'
        executePolicyMonth: this.get('protection.HyperCDPSchedule_task_create_step3_executePolicy_month_lab'),
        // 保留个数
        retainNum: this.get('protection.HyperCDPSchedule_task_create_step2_numofsaving_lab', true),

        retentionNumTip: this.get('protection.HyperCDPSchedule_task_create_step2_cycle_numofsaving_introduction_tip'),
        retentionNumTip2: this.get('protection.HyperCDPSchedule_task_fs_create_step2_cycle_numofsaving_introduction_tip'),
        retentionTimeTip: this.get('protection.HyperCDPSchedule_task_create_step2_retentionPeriodInteger_tip'),
        snapshotTag: this.get('protection.replicaiton_task_createAsync_userSnapSync_Tag_lab'),
        snapshotTagTip: this.get('protection.replicaiton_task_createAsync_userSnapSync_Tag_tip'),
        userSnapSyncTagValid: this.get('protection.replicaiton_task_createAsync_userSnapSync_Tag_valid'),

        retainedSnapshotData: this.get('protection.HyperCDPSchedule_task_modify_reservedSnap_lab'),
        yes: this.get('common.term_yes_label'),
        no: this.get('common.term_No_label'),
        retainedSnapshotErrorTip: this.get('protection.HyperCDPSchedule_task_modify_reservedSnap_err_info'),
    };

    create = {
        wormTab: this.get('protection.HyperCDPSchedule_task_create_step2_secureSnap_lab'),
        enableAutoDelete: this.get('service.filesystem_task_create_worm_para_enableAutoDelete_checkbox'),
        autoDeleteInfo: this.get('protection.HyperCDPSchedule_task_create_step2_autoDelete_info'),
        protectTime: this.get('service.filesystem_task_create_worm_para_protectTime_value_min_label'),
        nullValidation: (name: string): string => this.get('common.null_validation', [name]),
        protectTimeValid: (name: string, min: string, max: string): string =>
            this.get('service.filesystem_task_create_worm_para_protectTime_valid', [name, min, max]),
        enableLab: this.get('common.enable_label'),
        protectPeriodRangeLabel: (min: string, max: string): string => this.get('common.valueRange_label', [min, max]),
        modifyHighRisk: (name: string): string => this.get('protection.dataProtection_term_HyperCDP_Schedule_modifySecureSnap_msg', [name]),
        secureSnapEnabledTips: this.get('protection.dataProtection_term_FSHyperCDP_SCHEDULE_enableSecureSnap_msg'),
        modifyFsHighRisk: (name: string): string =>
            this.get('protection.dataProtection_term_FSHyperCDP_SCHEDULE_modifySecureSnap_msg', [name]),
        limitedNumHighRisk: this.get('protection.HyperCDPSchedule_task_modify_confirm_msg'),
        reservedSnapHighRisk: this.get('protection.HyperCDPSchedule_task_modify_reservedSnap_confirm_msg'),
    };

    modify = {
        autoDeleteInfo: this.get('protection.HyperCDPSchedule_task_modify_autoDelete_info'),
    };

    // 请设置策略执行时间。
    specifyTimeErr = this.get('protection.HyperCDPSchedule_task_create_step2_weeklyExecute_tip');

    cdpConfigPolicySTip = this.get('protection.HyperCDPSchedule_task_create_executePeriod_info_simple');
    // 最多配置四个策略
    atMostFourCdpSchedule = this.i18nService.get('service.Cloudtier_task_policy_maxNum_info', [HyperCdpConfigPolicySNumberRange.MAX]);
    // 至少配置一个策略
    atLeastOneCdpSchedule = this.get('protection.HyperCDPSchedule_task_create_step2_backupPolicy_retention_tip');
    secondsOpts: SelectOption[] = [
        {
            label: '10',
            value: 10,
        },
        {
            label: '20',
            value: 20,
        },
        {
            label: '30',
            value: 30,
        },
        {
            label: '40',
            value: 40,
        },
        {
            label: '50',
            value: 50,
        },
    ];
    everyOpts: SelectOption[] = [
        {
            label: this.title.second,
            value: 'second',
        },
        {
            label: this.title.minute,
            value: 'minute',
        },
        {
            label: this.title.hour,
            value: 'hour',
        },
    ];
    retentionOfEveryOpts: SelectOption[] = [
        {
            label: this.title.everyByTime,
            value: 'time',
        },
        {
            label: this.title.everyByCount,
            value: 'count',
        },
    ];
    hourList: SelectOption[] = [
        { label: '00:00', value: 0 },
        { label: '01:00', value: 1 },
        { label: '02:00', value: 2 },
        { label: '03:00', value: 3 },
        { label: '04:00', value: 4 },
        { label: '05:00', value: 5 },
        { label: '06:00', value: 6 },
        { label: '07:00', value: 7 },
        { label: '08:00', value: 8 },
        { label: '09:00', value: 9 },
        { label: '10:00', value: 10 },
        { label: '11:00', value: 11 },
        { label: '12:00', value: 12 },
        { label: '13:00', value: 13 },
        { label: '14:00', value: 14 },
        { label: '15:00', value: 15 },
        { label: '16:00', value: 16 },
        { label: '17:00', value: 17 },
        { label: '18:00', value: 18 },
        { label: '19:00', value: 19 },
        { label: '20:00', value: 20 },
        { label: '21:00', value: 21 },
        { label: '22:00', value: 22 },
        { label: '23:00', value: 23 },
    ];
    dateOpt: SelectOption[] = [
        {
            label: this.title.sun,
            value: 0,
        }, {
            label: this.title.mon,
            value: 1,
        }, {
            label: this.title.tues,
            value: 2,
        }, {
            label: this.title.wed,
            value: 3,
        }, {
            label: this.title.thrs,
            value: 4,
        }, {
            label: this.title.fri,
            value: 5,
        }, {
            label: this.title.sat,
            value: 6,
        },
    ];

    err = {
        retention: this.get('protection.HyperCDPSchedule_task_create_step2_backupPolicy_retentionPeriod_zero_tip'),
        notTimes: this.get('protection.HyperCDPSchedule_task_create_step2_retentionPeriodInteger_tip'),
        userSnapSyncTagValid: this.get('protection.replicaiton_task_createAsync_userSnapSync_Tag_valid'),
    };

    constructor(
        private i18nService: I18nService,
        private time: TimeService,
    ) { }

    isEn(): boolean {
        return this.i18nService.language === 'en';
    }

    nullErrMsg(name: string): string {
        const i18nName = this.title[name];
        return this.get('common.null_validation', [i18nName]);
    }
    lengthErrMsg(min: number, max: number): string {
        return this.get('common.length_msg', [min, max]);
    }
    nameErrMsg(name: string): string {
        const i18nName = this.title[name];
        return this.get('common.nameformat_validation', [i18nName]);
    }

    get(key: string, params?: any[] | boolean, colon?: boolean): string {
        return this.i18nService.get(key, params, colon);
    }
    renderTime(time: number): string {
        return time === 0 ?
            UNPLACEHOLDER :
            this.time.formatUTC(time * 1e3);
    }

    exceedErr(day: number, hour: number): string {
        return this.get('protection.HyperCDPSchedule_task_create_step2_backupPolicy_retentionPeriod_err_tip', [day, hour]);
    }

    /**
     * 检查最小保护周期时间合法性
     * #define WORM_CDP_MAX_PROTECT_PERIOD_YEARS   (70ULL)
     * #define WORM_CDP_MAX_PROTECT_PERIOD_MONTHS  (840ULL)
     * #define WORM_CDP_MAX_PROTECT_PERIOD_DAYS    (25550ULL)
     * define WORM_CDP_MIN_PROTECT_PERIOD_DAYS  (1ULL)
     */
    checkProtectTime(value: string, unit: string): { [key: string]: boolean } {
        let obj = null;
        switch (unit) {
            case protectPeriodUnitE.day:
                obj = Number(value) > 7300 || Number(value) < 1 ? { range: true } : null;
                break;
            case protectPeriodUnitE.month:
                obj = Number(value) > 240 || Number(value) < 1 ? { range: true } : null;
                break;
            case protectPeriodUnitE.year:
                obj = Number(value) > 20 || Number(value) < 1 ? { range: true } : null;
                break;
            default:
                break;
        }
        return obj;
    }

    /**
     * 最小保护周期校验
     */
    protectPeriodValidator = (control: AbstractControl): ValidationErrors | null => {
        const unit = control.root.get('PROTECTPERIODUNIT') as FormControl;
        if (!unit) {
            return null;
        }
        if (isUndefined(control.value) || control.value === '' || control.value === null) {
            return { required: true };
        } else {
            return this.checkProtectTime(control.value, unit.value);
        }
    };

    /**
     * 最小保护周期单位校验
     */
    protectPeriodUnitValidator = (control: AbstractControl): ValidationErrors | null => {
        const time = control.root.get('PROTECTPERIOD') as FormControl;
        if (!time) {
            return null;
        }
        if (isUndefined(time.value) || time.value === '' || time.value === null) {
            time.setErrors({ required: true });
            return null;
        }
        const obj = this.checkProtectTime(time.value, control.value);
        if (obj) {
            time.setErrors(obj);
        } else {
            time.clearError('range');
        }
        return null;
    };

    rangeByteLengthErr(min: number, max: number): string {
        return this.get('common.length_msg', [min, max]);
    }

    /**
     * 标签设置
     */
    snapTagValidator(type: { [key: string]: boolean }): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const reg = /(^[a-zA-Z0-9][a-zA-Z0-9-_.]*$)/;
            let isValid;
            if (control.value === '' || isUndefined(control.value)) {
                isValid = true;
            } else {
                isValid = reg.test(control.value);
            }
            return isValid ? null : type;
        };
    }

    /**
     * 执行周期选项
     */
    timeUnitOpts: SelectOption[] = [
        {
            label: this.title.second,
            value: FrequenciesE.SECONDLY,
        },
        {
            label: this.title.minute,
            value: FrequenciesE.MINUTELY,
        },
        {
            label: this.title.hour,
            value: FrequenciesE.HOURLY,
        },
        {
            label: this.title.day,
            value: FrequenciesE.DAILY,
        },
        {
            label: this.title.weeklyLab,
            value: FrequenciesE.WEEKLY,
        },
        {
            label: this.title.mouthlyLab,
            value: FrequenciesE.MONTHLY,
        },
    ];

    /**
     * 一天24小时选项
     */
    dailyHourOption(): SelectOption[] {
        const result: SelectOption[] = [];
        for (let index = 0; index < HyperCdpConfigPolicySHourValue.MAX; index++) {
            const labelStr = index > 9 ? index : `0${index}`;
            const item: SelectOption = {
                label: labelStr.toString(),
                value: index,
            };
            result.push(item);
        }

        return result;
    }
    /**
     * 每月选天选项
     */
    mouthlyDayOption(): SelectOption[] {
        const result: SelectOption[] = [];
        for (let index = 1; index <= MAX_HYPER_CDP_CONFIG_POLICY_SMOUTHLY_DAY; index++) {
            const labelStr = this.i18nService.get('protection.HyperCDPSchedule_task_create_someday_value_simple', [index]);
            const item: SelectOption = {
                label: labelStr,
                value: index,
            };
            result.push(item);
        }

        // 最后一天
        result.push(
            {
                label: this.title.lastday,
                value: CALENDAR_LAST_DAY,
            },
        );
        return result;
    }
}
